import type { BottomSheetModal } from '@gorhom/bottom-sheet';
import { BottomSheetBackdrop } from '@gorhom/bottom-sheet';
import { PinwheelExperienceInput } from '@varo/graphql-schema-types';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import Illustration from 'shared/components/vxl/Illustration/Illustration';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import useDirectDepositLoadingComponent from 'shared/hooks/pinwheel/useDirectDepositLoadingComponent/useDirectDepositLoadingComponent';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import theme from 'shared/styles/theme';

const PINWHEEL_SKIP_INTRO_DELAY_MS = 3000;

export interface MoveYourPayDirectDepositBottomSheetProps {
  bottomSheetRef: RefObject<BottomSheetModal | null>;
  onFinish: () => void;
  setIsBottomSheetOpened: Dispatch<SetStateAction<boolean>>;
}

const MoveYourPayDirectDepositBottomSheet = ({
  bottomSheetRef,
  onFinish,
  setIsBottomSheetOpened,
}: MoveYourPayDirectDepositBottomSheetProps) => {
  const { t: translatedText } = useTranslation('believe', {
    keyPrefix: 'moveYourPay.directDepositSheet',
  });
  const { initiatePinwheel, isPinwheelLoading, isPinwheelLaunched } =
    usePinwheel();
  const { skipIntroScreen } = useDirectDepositLoadingComponent();
  const hasFinishedRef = useRef(false);
  const isLaunchingPinwheelRef = useRef(false);

  useEffect(() => {
    if (isPinwheelLaunched) {
      bottomSheetRef.current?.dismiss();
    }
  }, [isPinwheelLaunched, bottomSheetRef]);

  const finish = () => {
    if (hasFinishedRef.current) {
      return;
    }
    hasFinishedRef.current = true;
    onFinish();
  };

  const handleDismiss = () => {
    if (isLaunchingPinwheelRef.current) {
      return;
    }
    finish();
  };

  const handleSkip = () => {
    bottomSheetRef.current?.dismiss();
  };

  const handleSetUpDirectDeposit = () => {
    isLaunchingPinwheelRef.current = true;
    initiatePinwheel({
      input: {
        experience: PinwheelExperienceInput.DirectDepositSwitch,
        skipIntroScreen,
      },
      launchBehavior: skipIntroScreen
        ? { delayAfterTokenMs: PINWHEEL_SKIP_INTRO_DELAY_MS }
        : undefined,
      onOutcome: finish,
      onLinkTokenError: finish,
    });
  };

  return (
    <BottomSheet
      ref={bottomSheetRef}
      analyticsId="moveYourPayDirectDepositSheetViewed"
      onDismiss={handleDismiss}
      setIsBottomSheetOpened={setIsBottomSheetOpened}
      enablePanDownToClose={!isPinwheelLoading}
      backdropComponent={(props) => (
        <BottomSheetBackdrop
          {...props}
          disappearsOnIndex={-1}
          appearsOnIndex={0}
          pressBehavior={isPinwheelLoading ? 'none' : 'close'}
          onPress={isPinwheelLoading ? undefined : handleDismiss}
        />
      )}
    >
      <View style={styles.contentContainer}>
        <View style={styles.illustrationContainer}>
          <Illustration illustration="PAYCHECK_A" />
        </View>
        <TitleLgText
          numberOfLines={0}
          style={styles.titleText}
          accessibilityRole="header"
        >
          {translatedText('title')}
        </TitleLgText>
      </View>
      <View style={styles.buttonContainer}>
        <Button
          label={translatedText('setUpButton')}
          onPress={handleSetUpDirectDeposit}
          isLoading={isPinwheelLoading}
          analyticsId="moveYourPayDirectDepositSetUpClicked"
          customAttributes={{ cta: 'Set up direct deposit' }}
        />
        <Button
          label={translatedText('skipButton')}
          onPress={handleSkip}
          isDisabled={isPinwheelLoading}
          type="TEXT"
          analyticsId="moveYourPayDirectDepositSkipped"
          customAttributes={{ cta: 'Skip for now' }}
        />
      </View>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    gap: theme.spacing.s,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  contentContainer: {
    alignItems: 'center',
    gap: theme.spacing.m,
    paddingHorizontal: theme.spacing.l,
    paddingTop: theme.spacing.s,
  },
  illustrationContainer: {
    height: 72,
    width: 72,
  },
  titleText: {
    textAlign: 'center',
  },
});

export default MoveYourPayDirectDepositBottomSheet;
